import * as PIXI from "pixi.js"
import {GameDispatcher} from "./base/GameDispatcher";
import {GameScreenNavigator} from "./base/GameScreenNavigator";
import {AbstractApp} from "../app/AbstractApp";
import {PixiAssetsLoader} from "./assets";
import {GameJuggler} from "./base";
import {Rectangle} from "pixi.js";

export type PixiAssetsScaleFactor = 2 | 4;

export abstract class AbstractPixiRoot extends GameScreenNavigator {

  readonly app:AbstractApp;
  readonly pixi: PIXI.Application;

  readonly onScreenHierarchyChanged = new GameDispatcher(this);
  readonly webglcontextlost = new GameDispatcher();
  readonly webglcontextrestored = new GameDispatcher();

  protected _currentPixiWidth = 0;
  protected _currentPixiHeight = 0;

  // 1536 - for scaleFactor 4; 768 - for scaleFactor 2;
  protected _scaleFactor:PixiAssetsScaleFactor = 4;
  protected _fixedStageWidth = 0;
  protected _safeAreaTop = 0;
  protected _safeAreaBottom = 0;
  protected _fixedStageHeight = 0;
  protected _isInitialized:boolean = false;

  protected _pixiContainer!:HTMLElement;
  protected _pixiContainerResizeObserver:ResizeObserver | null = null;

  protected static _isLoadKTX2Initialized = false;

  constructor(app:AbstractApp) {
    super();
    this.root = this;
    this._juggler = new GameJuggler(this, null);
    this.app = app;
    this.pixi = new PIXI.Application();
  }

  get isInitialized() {
    return this._isInitialized;
  }

  override destroy() {
    super.destroy();
    this.onScreenHierarchyChanged.destroy();
    this._pixiContainerResizeObserver?.unobserve(this._pixiContainer);
    this._pixiContainerResizeObserver?.disconnect();
    this.app.device.onActivateEvent.remove(this.onActivate);
    this.app.device.onDeactivateEvent.remove(this.onDeActivate);
    this.app.device.onExit.remove(this.onExit);
    this.app.device.onWindowResize.remove(this.onResize);
  }

  get scaleFactor() {
    return this._scaleFactor;
  }

  static multiply(scaleFactor:PixiAssetsScaleFactor) {
    if (scaleFactor == 4) {
      return 1;
    }
    if (scaleFactor == 2) {
      return .5;
    }
    return 1;
  }

  get fixedStageWidth() {
    return this._fixedStageWidth;
  }

  get fixedStageHeight() {
    return this._fixedStageHeight;
  }

  get safeZoneBottom() {
    return this._safeAreaBottom;
  }

  get safeZoneTop() {
    return this._safeAreaTop;
  }

  get fixedStageSafeHeight() {
    return this._fixedStageHeight - this.safeZoneBottom - this.safeZoneTop;
  }

  adapt(size: number) {
    if (this.scaleFactor == 4) {
      return size;
    }
    if (this.scaleFactor == 2) {
      return .5 * size;
    }
    return size;
  }

  deAdaptsize(size: number) {
    if (this.scaleFactor == 4)
      return size;
    if (this.scaleFactor == 2)
      return 2 * size;
    return size;
  }

  lock() {
    this.interactive = false;
  }

  unlock() {
    this.interactive = true;
  }
  
  get uiLib() {
    return this.assetsLoader!.getFlumpLib('ui')!;
  }

  get mainTextLib() {
    return this.assetsLoader!.getTextLib('main')!;
  }

  get dialogTitleTextLib() {
    return this.assetsLoader!.getTextLib('dialogTitle')!;
  }

  public async init(pixiContainer: HTMLElement, options: {
    backgroundColor: number,
    backgroundAlpha: number,
  } = {
    backgroundAlpha: 1,
    backgroundColor: 0xfbf4d9
  }) {
    if (this._isInitialized) {
      return;
    }

    this._pixiContainer = pixiContainer;

    await this.pixi.init({
      width: this._pixiContainer.clientWidth,
      height: this._pixiContainer.clientHeight,
      backgroundColor: options.backgroundColor,
      backgroundAlpha: options.backgroundAlpha,
      resolution: this.app.device.devicePixelRatio, // влияет только на четкость изображения, оставить так
      autoDensity: true,
    });

    this._pixiContainer.appendChild(this.pixi.canvas);
    this.pixi.stage.addChild(this);

    // this.pixi.stage.boundsArea = new Rectangle(0, 0, this._pixiContainer.clientWidth, this._pixiContainer.clientHeight);
    // this.boundsArea = new Rectangle(0, 0, this._pixiContainer.clientWidth, this._pixiContainer.clientHeight);

    // CONTEXT LOSS HANDLING
    this.pixi.canvas.addEventListener("webglcontextlost", (e) => {
      // window.location.reload();
      this.webglcontextlost.dispatch();
    });
    this.pixi.canvas.addEventListener("webglcontextrestored", (e) => {
      // SandboxPixiRoot.log('CONTEXT RESTORED');
      this.webglcontextrestored.dispatch();
    });

    const physicalHeight = window.screen.height * this.app.device.devicePixelRatio;
    if (physicalHeight >= (this.app.device.isAndroid ? 1280 : 1120)) {
      this._scaleFactor = 4;
      this._fixedStageWidth = 1536;
      this._fixedStageHeight = 2048;
    } else {
      this._scaleFactor = 2;
      this._fixedStageWidth = 768;
      this._fixedStageHeight = 1024;
    }

    this._pixiContainerResizeObserver = new ResizeObserver((entries) => {
      this.onResize();
    });
    this._pixiContainerResizeObserver.observe(this._pixiContainer);
    this.app.device.onWindowResize.add(this.onResize, this);
    this.onResize();

    this.pixi.ticker.minFPS = 60;
    this.pixi.ticker.maxFPS = 60;
    this.pixi.ticker.add((now) => {
      this.juggler?.advanceTime(now.deltaMS / 1000);
    });

    if (!AbstractPixiRoot._isLoadKTX2Initialized) {
      AbstractPixiRoot._isLoadKTX2Initialized = true;
      PIXI.extensions.add(
          PIXI.loadKTX2,
      );
    }

    this._isInitialized = true;
  }

  override onResize() {
    let newWidth = this._pixiContainer.clientWidth;
    let newHeight = this._pixiContainer.clientHeight;

    if (this._currentPixiWidth != newWidth || this._currentPixiHeight != newHeight) {
      this._currentPixiWidth = newWidth;
      this._currentPixiHeight = newHeight;

      this._safeAreaTop = this.app.device.safeAreaTop;
      this._safeAreaBottom = this.app.device.safeAreaBottom;

      // this.pixi.stage.boundsArea.width = newWidth;
      // this.pixi.stage.boundsArea.height = newHeight;

      this.pixi.renderer.resize(
          newWidth,
          newHeight
      );

      let rootScale = newHeight / this._fixedStageHeight;
      this._fixedStageWidth = newWidth / rootScale;
      this.scale = rootScale;


      // todo ??? расчитывать четко по краям экрана
      // this.boundsArea.width = this._fixedStageWidth / rootScale;
      // this.boundsArea.height = this._fixedStageHeight / rootScale;

      super.onResize();
    }
  }
  
  public createTextureByColor(color:PIXI.Color){
    const graphics = new PIXI.Graphics();
    graphics.beginFill(color); // Красный цвет
    graphics.drawRect(0, 0, 1, 1); // Рисуем прямоугольник 100x100
    graphics.endFill();

    const renderTexture = PIXI.RenderTexture.create({ width: 1, height: 1 });
    this.pixi.renderer.render(graphics, { renderTexture });
    return new PIXI.Texture(renderTexture);
  }
}
